import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpdateTimelineActivityViewModel } from "../../types/viewModels/timelineActivity/UpdateTimelineActivityViewModel";
import type { AddUpdateTimelineActivityViewModel } from "../../types/viewModels/timelineActivity/AddUpdateTimelineActivityViewModel";
import type { TimelineActivityDTO } from "../../types/dtos/TimelineActivityDTO";
import type { RecordId } from "@repo/types/ui/tables";
import { objectToFormData } from "../../helpers/objectToFormData";
import { useApiAccess } from "@repo/hooks/contexts";
import { timelineActivityKeys } from "@repo/types/queries";
import type { ExecutionResponse } from "@repo/types/responses";

export default function useGetTimelineActivitiesByTicketId(
  ticketId: RecordId,
  options?: { enabled: boolean },
) {
  const { apiClient } = useApiAccess();

  return useQuery<TimelineActivityDTO[]>({
    queryKey: timelineActivityKeys.listByRelationId(ticketId),
    queryFn: async () => {
      const response = await apiClient.get<TimelineActivityDTO[]>(
        `/timelineActivity/getByTicketId/${ticketId}`,
      );

      return response.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateTimelineActivity() {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (model: UpdateTimelineActivityViewModel) => {
      const formData = objectToFormData(model);

      const response = await apiClient.post<ExecutionResponse>(
        `/timelineActivity/addUpdate`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data;
    },
    onSuccess: (_, timelineActivity) => {
      timelineActivityKeys.invalidateListByRelationId(
        queryClient,
        timelineActivity.TicketId,
      );
    },
  });
}

export function useAddUpdateTimelineActivity() {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (model: AddUpdateTimelineActivityViewModel) => {
      const formData = objectToFormData(model);

      const response = await apiClient.post<ExecutionResponse>(
        `/timelineActivity/addUpdate`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data;
    },
    onSuccess: (_, timelineActivity) => {
      timelineActivityKeys.invalidateListByRelationId(
        queryClient,
        timelineActivity.TicketId,
      );
    },
  });
}
